import { ArrowLeftRight } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { type DiffLine } from '@/pages/company/roles/rolesLogic';

interface DiffConfirmDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  roleName: string;
  lines: DiffLine[];
  memberCount: number;
  saving?: boolean;
  onConfirm: () => void;
}

/**
 * DiffConfirmDialog — обязательный шаг сохранения правки роли (пункт 6, AK-343).
 *
 * Показывает «что изменится» человекочитаемо (+Техника C/U, −Отчёты D) и
 * «сколько участников затронет» ДО применения. Снимает риск ADR «40 чекбоксов
 * вслепую». Данные — из computeMatrixDiff + summarizeDiff + members-preview.
 */
export function DiffConfirmDialog({
  open,
  onOpenChange,
  roleName,
  lines,
  memberCount,
  saving = false,
  onConfirm,
}: DiffConfirmDialogProps) {
  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[460px]">
        <DialogHeader>
          <span className="mb-1 flex h-10 w-10 items-center justify-center rounded-lg bg-accent-brand/15 text-accent-brand">
            <ArrowLeftRight className="h-5 w-5" />
          </span>
          <DialogTitle>Сохранить изменения роли «{roleName}»?</DialogTitle>
          <DialogDescription>Вы меняете права роли. Вот что изменится:</DialogDescription>
        </DialogHeader>

        <div className="rounded-lg border border-accent-brand/40 bg-accent-brand/10 px-3.5 py-3">
          <div className="mb-2 flex items-center gap-2 text-xs font-bold text-accent-brand">
            <ArrowLeftRight className="h-3.5 w-3.5" /> Изменения прав
          </div>
          <div className="space-y-1.5">
            {lines.map((line) => (
              <div
                key={`${line.resource}:${line.added ? '+' : '-'}`}
                className="flex items-start gap-2 text-sm"
              >
                <span
                  className={
                    line.added
                      ? 'mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded bg-accent-brand text-xs font-extrabold text-accent-brand-foreground'
                      : 'mt-0.5 flex h-[18px] w-[18px] flex-none items-center justify-center rounded bg-destructive text-xs font-extrabold text-destructive-foreground'
                  }
                >
                  {line.added ? '+' : '−'}
                </span>
                <span className="text-foreground">
                  <b className="font-semibold">{line.resourceName}</b> — {line.text}
                </span>
              </div>
            ))}
          </div>
          <div className="mt-2.5 border-t border-accent-brand/40 pt-2.5 text-[12.5px] text-accent-brand">
            Затронет{' '}
            <b className="font-semibold">
              {memberCount} {pluralMembers(memberCount)}
            </b>{' '}
            на этой роли. Доступ обновится сразу после сохранения.
          </div>
        </div>

        <DialogFooter>
          <Button variant="ghost" onClick={() => onOpenChange(false)} disabled={saving}>
            Назад к правке
          </Button>
          <Button onClick={onConfirm} disabled={saving}>
            {saving ? 'Сохраняем…' : 'Сохранить и применить'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function pluralMembers(n: number): string {
  const mod10 = n % 10;
  const mod100 = n % 100;
  if (mod10 === 1 && mod100 !== 11) return 'участника';
  if (mod10 >= 2 && mod10 <= 4 && (mod100 < 10 || mod100 >= 20)) return 'участников';
  return 'участников';
}
